import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Clock, MessageCircle, ArrowRight, Check, Calendar, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";

export default function ThankYouPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const name = params.get("name") || "";
  const tripName = params.get("trip") || "";
  const firstName = name.split(" ")[0] || "";

  const steps = [
    {
      icon: Check,
      title: "Hemos recibido tu solicitud",
      body:
        "Está en cola de nuestros asesores familiares. No es un email automático — la lee una persona real.",
    },
    {
      icon: Phone,
      title: "Te llamamos en horario de oficina",
      body:
        "Normalmente en las próximas horas. Si nos escribes fuera de horario, te llamaremos a primera hora del día laborable siguiente.",
    },
    {
      icon: Calendar,
      title: "Diseñamos tu viaje sin compromiso",
      body:
        "Escuchamos a tu familia, resolvemos dudas y te enviamos un presupuesto detallado. Solo reservas si te convence.",
    },
  ];

  return (
    <>
      <SEO
        title="Gracias por confiar en Club Amistad · Solicitud recibida"
        description="Hemos recibido tu solicitud. Un asesor familiar te llamará en breve para resolver tus dudas y diseñar tu viaje de esquí a medida."
        path="/gracias-formulario"
        noindex
      />

      {/* Google Ads / Meta Pixel — dispara evento de conversión al cargar esta página.
          Los IDs se activan cuando conectemos GA4 / Ads / Meta. */}
      <div className="pt-[76px] md:pt-[96px] min-h-screen bg-brand-bg" data-testid="thank-you-page">
        <section className="section-y">
          <div className="container-prose max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-primary/10 text-brand-primary px-4 py-1.5 text-xs font-outfit font-semibold tracking-wide uppercase mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                Solicitud recibida
              </div>

              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-brand-primary">
                {firstName ? (
                  <>
                    Gracias, <span className="text-brand-accent italic font-cormorant">{firstName}</span>.
                  </>
                ) : (
                  <>
                    Gracias por confiar en{" "}
                    <span className="text-brand-accent italic font-cormorant">nosotros</span>.
                  </>
                )}
              </h1>
              <p className="mt-6 max-w-xl mx-auto font-manrope text-base md:text-lg text-brand-ink/80 leading-relaxed">
                Ya tenemos tu mensaje
                {tripName ? (
                  <>
                    {" "}sobre <span className="font-semibold text-brand-primary">{tripName}</span>
                  </>
                ) : null}
                . Un asesor familiar de Club Amistad te llamará muy pronto para resolver tus dudas y
                diseñar el viaje contigo. Sin compromiso, sin letra pequeña.
              </p>
            </motion.div>

            {/* NEXT STEPS TIMELINE */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="mt-12 md:mt-16 grid md:grid-cols-3 gap-5"
            >
              {steps.map((s, i) => (
                <div
                  key={i}
                  data-testid={`next-step-${i}`}
                  className="card-soft p-6 md:p-7 relative"
                >
                  <div className="absolute -top-3 left-6 h-6 w-6 rounded-full bg-brand-primary text-white text-xs font-outfit font-semibold flex items-center justify-center">
                    {i + 1}
                  </div>
                  <s.icon className="h-6 w-6 text-brand-accent mb-4" strokeWidth={2} />
                  <h3 className="font-outfit font-semibold text-brand-primary text-lg mb-2">
                    {s.title}
                  </h3>
                  <p className="text-sm text-brand-ink/75 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </motion.div>

            {/* CONTACT CARDS */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-12 md:mt-14 rounded-3xl border border-brand-line bg-brand-surface/60 p-6 md:p-8"
            >
              <p className="overline">Mientras tanto</p>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-brand-primary mt-2 mb-6">
                ¿Necesitas hablar con nosotros antes?
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <a
                  href="tel:+34914020331"
                  data-testid="thanks-call-cta"
                  className="flex items-center gap-4 rounded-2xl bg-white border border-brand-line hover:border-brand-accent transition-colors p-5"
                >
                  <div className="h-11 w-11 rounded-full bg-[#E6217A] text-white flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-outfit font-semibold text-brand-primary text-base">
                      914 020 331
                    </p>
                    <p className="text-xs text-brand-muted flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3" /> L-V · 10-19 h · Verano 10-14 h
                    </p>
                  </div>
                </a>
                <div
                  className="flex items-center gap-4 rounded-2xl bg-white border border-brand-line p-5"
                >
                  <div className="h-11 w-11 rounded-full bg-brand-primary text-white flex items-center justify-center shrink-0">
                    <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-outfit font-semibold text-brand-primary text-base">
                      Chatea con Aitana
                    </p>
                    <p className="text-xs text-brand-muted mt-0.5">
                      Usa el chat de la esquina — te responde 24 h al día.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* SECONDARY CTA */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mt-10 md:mt-14 text-center"
            >
              <Link
                to="/#viajes"
                data-testid="thanks-back-trips"
                className="inline-flex items-center gap-2 font-outfit font-semibold text-brand-primary hover:text-brand-accent transition-colors group"
              >
                Volver a ver todos nuestros viajes
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
